import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Typography } from "antd";

import type { ScratchCardRewardBlock } from "@/api/warranty";
import { DEFAULT_HOTLINE } from "@/constants";
import ScratchCardConfirmModal from "./ScratchCardConfirmModal";
import viettelLogo from "@/assets/images/logo_nha_mang/vettel.png";
import mobifoneLogo from "@/assets/images/logo_nha_mang/mobifone.png";
import vinaphoneLogo from "@/assets/images/logo_nha_mang/vinaphone.png";
import vietnamobileLogo from "@/assets/images/logo_nha_mang/vietnammobie.png";
import gmobileLogo from "@/assets/images/logo_nha_mang/gtel.png";
import "./scratch-card-reward.css";

const { Text } = Typography;

export interface ScratchCardRewardCardProps {
  reward: ScratchCardRewardBlock;
  /** Đang gọi API chọn nhà mạng / phát thẻ / nhập SĐT. */
  selecting: boolean;
  /** Chế độ xem trước — không cho thao tác. */
  preview?: boolean;
  onSelectTelecom: (network: number) => void;
  /** Luồng nạp tự động: khách nhập SĐT để nhận nạp (D6). */
  onSetPhone?: (phone: string) => void;
  /**
   * Vừa nhận thưởng TRONG phiên này -> chúc mừng. Mở lại trang (false) thì luồng nạp
   * tự động chỉ hiện trạng thái trả thưởng, không chúc mừng lại.
   */
  justRewarded?: boolean;
}

/** SĐT VN hợp lệ (đầu 0 hoặc +84), 9–11 chữ số sau đầu số. */
const isValidVnPhone = (raw: string): boolean =>
  /^(0|\+?84)(\d{9,10})$/.test(raw.replace(/[\s.]/g, ""));

const formatVnd = (value: number): string =>
  value > 0 ? new Intl.NumberFormat("vi-VN").format(value) + " đ" : "";

/** Logo theo mã nhà mạng của đối tác (1..5); fallback theo tên nếu số lệch. */
const LOGO_BY_NETWORK: Record<number, string> = {
  1: viettelLogo,
  2: mobifoneLogo,
  3: vinaphoneLogo,
  4: vietnamobileLogo,
  5: gmobileLogo,
};

const logoFor = (network: number | null, name?: string | null): string | undefined => {
  if (network !== null && LOGO_BY_NETWORK[network]) return LOGO_BY_NETWORK[network];
  const n = (name ?? "").toLowerCase();
  if (n.includes("viettel")) return viettelLogo;
  if (n.includes("mobi") && !n.includes("vietnam")) return mobifoneLogo;
  if (n.includes("vina")) return vinaphoneLogo;
  if (n.includes("vietnam")) return vietnamobileLogo;
  if (n.includes("gtel") || n.includes("gmobile")) return gmobileLogo;
  return undefined;
};

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 12.5l5 5L20 6.5" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Khối thẻ cào trên trang bảo hành GoldenPen (C12–C14):
 *  - cần chọn nhà mạng : lưới logo nhà mạng chọn được + nút "Nhận thẻ" (không đổi lại).
 *  - issued  : voucher hiện logo nhà mạng + mệnh giá + serial + mã thẻ (copy) + HSD.
 *  - lỗi     : cảnh báo + thử lại với nhà mạng đã chọn, hoặc chọn lại nhà mạng khác
 *              (kể cả failed, mỗi lần tính 1 lượt, tối đa 5 lượt).
 *  - đang xử lý : chờ phát.
 */
const ScratchCardRewardCard: React.FC<ScratchCardRewardCardProps> = ({
  reward,
  selecting,
  preview = false,
  onSelectTelecom,
  onSetPhone,
  justRewarded = false,
}) => {
  // Lỗi phát thưởng luôn là lỗi hệ thống/đối tác — khách không xử lý được và
  // `reward.error` chứa chi tiết kỹ thuật (mã lỗi đối tác, mệnh giá, ngân sách).
  // Chỉ báo chung + hotline; chi tiết đã có trong provider log cho admin.
  const errorHelp = `Rất tiếc, phần thưởng chưa được trả. Vui lòng liên hệ ${DEFAULT_HOTLINE} để được hỗ trợ.`;
  const [picked, setPicked] = useState<number | undefined>(
    reward.available_networks[0]?.network,
  );
  const [phone, setPhone] = useState("");
  // Popup xác nhận trước khi phát thẻ (không đổi lại được nhà mạng sau khi nhận),
  // buộc khách kiểm tra lại đúng nhà mạng đang dùng rồi mới bấm "Xác nhận".
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Phát thẻ lỗi -> khách mở lại bộ chọn để nhận thẻ bằng nhà mạng khác.
  const [reselecting, setReselecting] = useState(false);

  const pickedNetwork =
    picked !== undefined
      ? reward.available_networks.find((n) => n.network === picked)
      : undefined;

  // Modal mở suốt lúc gọi API (spinner trên nút). Khi API xong: thành công thì cả
  // khối chọn nhà mạng unmount (hiện voucher), lỗi thì đóng modal để lộ alert lỗi.
  const wasSelecting = useRef(false);
  useEffect(() => {
    if (wasSelecting.current && !selecting) {
      setConfirmOpen(false);
      setReselecting(false);
    }
    wasSelecting.current = selecting;
  }, [selecting]);

  const confirmSelectTelecom = () => {
    if (picked !== undefined) onSelectTelecom(picked);
  };

  // Ghi chú khách hàng (cả 2 loại phát thưởng). Lần kích hoạt đầu (justRewarded)
  // -> callout nổi bật cùng khối trả thưởng; các lần sau -> help text dưới thông tin.
  const noteMessage = reward.message?.trim();
  const noteBlock = noteMessage ? (
    <p className={`gp-message${justRewarded ? "" : " gp-message--help"}`}>{noteMessage}</p>
  ) : null;

  // --- Luồng NẠP TỰ ĐỘNG (topup): nhập SĐT / chờ nhận / đã nạp ---
  if (reward.reward_type === "topup") {
    const toppedUp = reward.status === "issued";
    const failed = reward.status === "failed";
    const phoneValid = isValidVnPhone(phone);
    // Đã chốt SĐT, đang chờ đối tác nạp (pending: chờ gửi lệnh; processing: đã gửi).
    // Khách không cần biết chi tiết kỹ thuật — chỉ cần biết đã trúng và khi nào nhận.
    const awaiting = !toppedUp && !failed && !reward.needs_phone && !reward.error;
    const needsAction = !!reward.needs_phone || !!reward.error;

    // Xem lại (mở lại trang, không phải vừa nhận trong phiên này): chỉ tra cứu trạng
    // thái trả thưởng — không chúc mừng lại. Vẫn nhường chỗ cho khối thao tác khi
    // khách còn phải nhập SĐT / cần biết lỗi.
    if (!justRewarded && !needsAction) {
      return (
        <section className="gp-reward" aria-label="Trạng thái trả thưởng">
          <div className="gp-ticket__body">
            <div className="gp-field">
              <span className="gp-field__k">Trạng thái trả thưởng</span>
              <span className="gp-field__v">{toppedUp ? "Đã trả thưởng" : "Chưa trả thưởng"}</span>
            </div>
            <div className="gp-field">
              <span className="gp-field__k">Mệnh giá thưởng</span>
              <span className="gp-field__v">{formatVnd(reward.card_amount)}</span>
            </div>
            {reward.phone && (
              <div className="gp-field">
                <span className="gp-field__k">SĐT nhận thưởng</span>
                <span className="gp-field__v">{reward.phone}</span>
              </div>
            )}
            {toppedUp && reward.issued_at && (
              <div className="gp-field">
                <span className="gp-field__k">Thời gian trả thưởng</span>
                <span className="gp-field__v">{reward.issued_at}</span>
              </div>
            )}
          </div>
          {noteBlock}
        </section>
      );
    }

    return (
      <section className="gp-reward" aria-label="Nạp tiền điện thoại khuyến mãi">
        <header className="gp-reward__head">
          <span className="gp-reward__ribbon">Quà tặng kích hoạt bảo hành</span>
          <div className="gp-reward__amount">{formatVnd(reward.card_amount)}</div>
          <div className="gp-reward__sub">Nạp tiền điện thoại</div>
        </header>

        {/* Đã nạp thành công */}
        {toppedUp && (
          <div className="gp-processing gp-processing--done">
            <span className="gp-net__check gp-net__check--inline"><CheckIcon /></span>
            Đã nạp {formatVnd(reward.card_amount)} vào số {reward.phone ?? ""}.
          </div>
        )}

        {/* Chờ nhận (bất đồng bộ) — chúc mừng + mệnh giá + mốc nhận 24h */}
        {awaiting && (
          <div className="gp-processing">
            <span className="gp-dot" />
            Chúc mừng bạn đã trúng thưởng {formatVnd(reward.card_amount)}. Phần thưởng sẽ được nạp vào
            số điện thoại {reward.phone ?? "của bạn"} trong vòng 24h.
          </div>
        )}

        {/* Nhập SĐT để nhận nạp (D6) */}
        {!toppedUp && !failed && reward.needs_phone && (
          <div>
            <p className="gp-picker__label">Nhập số điện thoại để nhận nạp tiền</p>
            <Input
              inputMode="numeric"
              size="large"
              placeholder="Số điện thoại nhận nạp"
              value={phone}
              disabled={preview || selecting}
              maxLength={13}
              onChange={(e) => setPhone(e.target.value)}
              onPressEnter={() => phoneValid && onSetPhone?.(phone)}
            />
            <Button
              type="primary"
              danger
              className="gp-cta w-full mt-3"
              size="large"
              loading={selecting}
              disabled={preview || !phoneValid}
              onClick={() => phoneValid && onSetPhone?.(phone)}
            >
              Nhận nạp
            </Button>
            <p className="gp-note">Số tiền sẽ được nạp trực tiếp vào số điện thoại này. Vui lòng nhập chính xác.</p>
          </div>
        )}

        {/* Lỗi nạp */}
        {!toppedUp && !reward.needs_phone && !!reward.error && (
          <div className="mt-3">
            <Alert type="error" showIcon message="Phát thưởng không thành công" description={errorHelp} />
          </div>
        )}

        {/* Ghi chú khách hàng */}
        {noteBlock}
      </section>
    );
  }

  const isIssued = reward.status === "issued";
  const showPicker = !isIssued && reward.needs_network_selection && reward.available_networks.length > 0;
  // Thẻ failed (kể cả không kèm error) vẫn là lỗi phát — cho khách thử lại nếu còn lượt.
  // manual_review: đối tác đã phát mà mất mã thẻ — chỉ báo lỗi + hotline (BE trả retries_left = 0).
  const showError =
    !isIssued && (!!reward.error || reward.status === "failed" || reward.status === "manual_review");
  const canRetry = reward.network !== null && (reward.retries_left ?? 0) > 0;
  const canReselect = canRetry && reward.available_networks.length > 1;
  const showReselect = showError && canReselect && reselecting;
  const showProcessing = !isIssued && !showPicker && !showError;

  const openReselect = () => {
    const others = reward.available_networks.filter((n) => n.network !== reward.network);
    setPicked(others[0]?.network);
    setReselecting(true);
  };

  const issuedLogo = logoFor(reward.network, reward.network_name);

  return (
    <section className="gp-reward" aria-label="Thẻ cào khuyến mãi">
      <header className="gp-reward__head">
        <span className="gp-reward__ribbon">Quà tặng kích hoạt bảo hành</span>
        <div className="gp-reward__amount">{formatVnd(reward.card_amount)}</div>
        <div className="gp-reward__sub">Thẻ cào điện thoại</div>
      </header>

      {/* --- Bước chọn nhà mạng (C14) --- */}
      {(showPicker || showReselect) && (
        <div>
          <p className="gp-picker__label">
            {showReselect ? "Chọn lại nhà mạng để nhận thẻ" : "Chọn nhà mạng để nhận thẻ"}
          </p>
          <div className="gp-net-grid" role="radiogroup" aria-label="Chọn nhà mạng">
            {reward.available_networks.map((n) => {
              const selected = picked === n.network;
              const logo = logoFor(n.network, n.name);
              const disabled = preview || selecting;
              return (
                <button
                  type="button"
                  key={n.network}
                  role="radio"
                  aria-checked={selected}
                  aria-label={n.name}
                  disabled={disabled}
                  className={`gp-net${selected ? " is-selected" : ""}${disabled ? " is-disabled" : ""}`}
                  onClick={() => setPicked(n.network)}
                >
                  <span className="gp-net__check"><CheckIcon /></span>
                  {logo ? (
                    <img className="gp-net__logo" src={logo} alt={n.name} />
                  ) : (
                    <span className="gp-net__name">{n.name}</span>
                  )}
                </button>
              );
            })}
          </div>

          <Button
            type="primary"
            danger
            className="gp-cta w-full mt-3"
            size="large"
            loading={selecting}
            disabled={preview || picked === undefined}
            onClick={() => picked !== undefined && setConfirmOpen(true)}
          >
            Nhận thẻ
          </Button>
          {showReselect ? (
            <>
              <Button
                className="gp-cta gp-cta--ghost w-full"
                size="large"
                disabled={selecting}
                onClick={() => setReselecting(false)}
              >
                Quay lại
              </Button>
              <p className="gp-note">
                Mỗi lần nhận lại tính 1 lượt (còn {reward.retries_left} lượt). Không thể đổi lại nhà mạng sau khi nhận thẻ.
              </p>
            </>
          ) : (
            <p className="gp-note">Không thể đổi lại nhà mạng sau khi nhận thẻ.</p>
          )}

          {/* Popup xác nhận nhà mạng trước khi phát thẻ (không đổi lại được) */}
          {confirmOpen && pickedNetwork && (
            <ScratchCardConfirmModal
              carrierName={pickedNetwork.name}
              carrierLogo={logoFor(pickedNetwork.network, pickedNetwork.name)}
              rewardAmount={reward.card_amount}
              loading={selecting}
              onConfirm={confirmSelectTelecom}
              onReselect={() => setConfirmOpen(false)}
              onClose={() => setConfirmOpen(false)}
            />
          )}
        </div>
      )}

      {/* --- Đã phát: voucher --- */}
      {isIssued && (
        <div className="gp-ticket">
          <div className="gp-ticket__brand">
            {issuedLogo ? (
              <img src={issuedLogo} alt={reward.network_name ?? "Nhà mạng"} />
            ) : (
              reward.network_name && <span>{reward.network_name}</span>
            )}
          </div>
          <div className="gp-ticket__perf" />
          <div className="gp-ticket__body">
            {reward.card_serial && (
              <div className="gp-field">
                <span className="gp-field__k">Số serial</span>
                <Text className="gp-field__v gp-code" copyable={{ text: reward.card_serial }}>
                  {reward.card_serial}
                </Text>
              </div>
            )}
            {reward.card_code && (
              <div className="gp-field">
                <span className="gp-field__k">Mã thẻ</span>
                <Text className="gp-field__v gp-code gp-code--pin" copyable={{ text: reward.card_code }}>
                  {reward.card_code}
                </Text>
              </div>
            )}
            {reward.card_expired_at && (
              <div className="gp-field">
                <span className="gp-field__k">Hạn sử dụng</span>
                <span className="gp-field__v">{reward.card_expired_at}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Lỗi phát thẻ --- */}
      {showError && !showReselect && (
        <div className="mt-3">
          <Alert type="error" showIcon message="Phát thẻ chưa thành công" description={errorHelp} />
          {canRetry && (
            <Button
              type="primary"
              danger
              className="gp-cta w-full mt-3"
              size="large"
              loading={selecting}
              disabled={preview}
              onClick={() => reward.network !== null && onSelectTelecom(reward.network)}
            >
              Thử lại ({reward.retries_left} lượt)
            </Button>
          )}
          {canReselect && (
            <Button
              className="gp-cta gp-cta--ghost w-full"
              size="large"
              disabled={preview || selecting}
              onClick={openReselect}
            >
              Chọn lại nhà mạng
            </Button>
          )}
        </div>
      )}

      {/* --- Đang xử lý --- */}
      {showProcessing && (
        <div className="gp-processing">
          <span className="gp-dot" />
          Thẻ đang được xử lý, vui lòng quét lại mã sau ít phút để nhận.
        </div>
      )}

      {/* --- Ghi chú khách hàng --- */}
      {noteBlock}
    </section>
  );
};

export default ScratchCardRewardCard;
